import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import _ from "lodash";
import cx from "classnames";

import TextareaAutosize from "react-autosize-textarea";
import { BannerView } from "src/components/banner";
import { Dropdown } from "react-bootstrap";
import { SimulationMetricsCarousel } from "./metrics";

import Slide from "../../../components/slide";
import Text from "src/components/text";
import { Simulation, SimulationValidationError } from "src/types/coaching/simulation";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { Banner } from "src/types/common";
import { updateSimulationProperty } from "src/redux/actions/coaching/simulation";
import { EditCourseThumbnail } from "src/pages/coaching/manage/courses/edit/thumbnail";
import { SelectSkillsDialog } from "src/pages/coaching/manage/courses/edit/components/selectSkills";
import { CourseLearners } from "src/pages/coaching/manage/courses/edit/components/learners";
import { CALL_TYPES, DIFFICULTY_CHOICES, DURATION_CHOICES, TIME_BOUND_CHOICES } from "src/constants/simulation";
import { FileService } from "src/utils/file.service";
import { ErrorMessage } from "../../../components/error";
import { SimulationContext } from "../../context";
import { AssessmentConfiguration, AssessmentParameter } from "src/types/coaching/assessmentConfiguration";
import { useSpring, animated } from "@react-spring/web";
import SegmentButton from "src/components/segmentButton";

import styles from "./callSettings.module.scss";
import pluralize from "pluralize";
import Thumbnail from "src/components/thumbnail";
import { getCallType } from "../../helper";

type EntityType = "discovery" | "coldCall" | "elevator_pitch";

const languageOptions = [
  { label: 'English', value: 'en' },
  { label: 'Spanish', value: 'es' },
  { label: 'German', value: 'de' },
  { label: 'French', value: 'fr' },
  { label: 'Marathi', value: 'mr' },
];

interface CallSettingsProps {
  simulation: Simulation;
}

export const CallSettings: React.FC<CallSettingsProps> = ({ simulation }) => {
  const dispatch = useDispatch();
  const simulationContext = useContext(SimulationContext);

  const id = simulation.id;

  const learners = useSelector((state: any) => {
    return _.get(state, "courses.learners.items") || [];
  });

  const userProfile = useMemo(()=> _.get(simulation,"publishedBy.icon",null), [simulation])

  const persistedSimulation = useSelector((state: any) => {
    return _.get(state.coaching, `simulations.simulation.${simulation.id}.item`)
  })

  const published = useSelector((state: any) =>
    _.get(state, 'coaching.simulations.publish') 
  ); 

  const name = useMemo(() => {
    return _.get(simulation, "name") || "";
  }, [simulation]);

  const type = useMemo(() => _.get(simulation, "type",null), [simulation])

  const callType = useMemo(() => {
      const value = _.get(simulation, "type") || "";
      return CALL_TYPES.find((item) => item.id === value)?.title || "";
  }, [simulation]);


  const description = useMemo(() => {
    return _.get(simulation, "description") || "";
  }, [simulation]);

  const pitchScenario = useMemo(() => {
    return _.get(simulation, "pitchScenario") || "";
  }, [simulation]);

  const pitchAudience = useMemo(() => {
    return _.get(simulation, "pitchAudience") || "";
  }, [simulation]);

  const language = useMemo(() => {
    return _.get(simulation, "language") || "en";
  }, [simulation]);


  const banner = useMemo(() => {
    return _.get(simulation, "banner");
  }, [simulation]);

  const duration = useMemo(() => {
    return _.get(simulation, "duration");
  }, [simulation]);

  const timeBound = useMemo(() => {
    return _.get(simulation, "timeBound");
  }, [simulation]);

  const difficulty = useMemo(() => {
    return _.get(simulation, "difficulty");
  }, [simulation]);

  const format = useMemo(() => {
    return _.get(simulation, "format", "video");
  }, [simulation]);

  const publishedBy = _.get(simulation, "publishedBy");


  const activeScorecard = simulationContext.activeAssessmentScorecard || simulation.assessmentConfiguration;
  const parameters: AssessmentParameter[] = useMemo(() => {
    return activeScorecard?.parameters || [];
  }, [activeScorecard]);

  const nameInputRef = React.useRef<HTMLTextAreaElement>(null);
  const descriptionInputRef = React.useRef<any>(null);

  const nameError = useSelector((state: any) => {
    const errors = _.get(state, `coaching.simulations.edit.errors`) || [];
    return _.find(errors, { type: "call-settings", property: "name" });
  });

  const bannerError = useSelector((state: any) => {
    const errors = _.get(state, `coaching.simulations.edit.errors`) || [];
    return _.find(errors, { type: "call-settings", property: "banner" });
  });

  const errors = useSelector((state: any) =>
    _.get(state, "coaching.simulations.edit.errors") || []
  );
  
  const descriptionError = _.find(errors, {
    type: "call-settings",
    property: "description",
  });
  
  const pitchScenarioError = _.find(errors, {
    type: "call-settings",
    property: "pitchScenario",
  });
  
  const pitchAudienceError = _.find(errors, {
    type: "call-settings",
    property: "pitchAudience",
  });
  
  const isPublished = useMemo(()=> _.get(published,'published', false),[published]) as boolean
  const publishedSimulation = useMemo(() => _.get(published, 'item'), [published])


  const publishedByName = useMemo(() => {
    const source = _.get(publishedSimulation,"publishedBy") || _.get(persistedSimulation,"publishedBy");
    const firstName = _.get(source, "firstName", "");
    const lastName = _.get(source, "lastName", "");
    return `${firstName} ${lastName}`.trim();
  }, [publishedSimulation, persistedSimulation]);

  const pitchConfiguration = callType === "Elevator Pitch";

  const [editThumbnail, setEditThumbnail] = useState(false);
  const [showSkillSlider, setShowSkillSlider] = useState(false);

  const handlePropertyChange = (property: string, value: any) => {
    dispatch(updateSimulationProperty({
      simulation: simulation.id!,
      property,
      value,
    }),
    );
  };

  const handleNameChange = (e: any) => {
    let input = e.target.value;
    input = input.replace(/\n/g, "");
    handlePropertyChange("name", input);
  };

  const handleDescriptionChange = (e: any) => {
    console.log("Description changed", e.target.value);
    const content = e.target.value;
    handlePropertyChange("description", content);
  };

  const handlePitchScenarioChange = (e: any) => {
    const content = e.target.value;
    handlePropertyChange("pitchScenario", content);
  }

  const handlePitchAudienceChange = (e: any) => {
    const content = e.target.value;
    handlePropertyChange("pitchAudience", content);
  }

  const handleFormatChange = (value: string) => {
    handlePropertyChange("format", value);
  };

  const handleThumbnailChange = (banner: Banner) => {
    handlePropertyChange("banner", banner);
    setEditThumbnail(false);
  };

  const handleDurationChange = (duration: any) => {
    handlePropertyChange("duration", duration);
  };
  
  const handleTimeBoundChange = (timeBound: any) => {
    handlePropertyChange("timeBound", timeBound);
  };

  const handleDifficultyChange = (difficulty: any) => {
    handlePropertyChange("difficulty", difficulty);
  };

  const handleAddSkillsFinish = (skills) => {
    handlePropertyChange("skills", skills);
  };

  const onSkillClicked = () => {
    setShowSkillSlider(true);
  }

  const handleSkillRemove = (skill: any) => {
    const items = simulation.skills?.filter((item: any) => item.id !== skill.id);
    handlePropertyChange("skills", items);
  };

  const handleEditAssessmentScoreCard = (value: boolean) => {
    simulationContext.editAssessmentScoreCard?.(value);
  }
  
  const subParameterCount = parameters.reduce(
    (sum, item) => sum + item.parameters.length,
    0
  );
  useEffect(() => {
    return () => {
      console.log("Unmounting CallSettings");
    }
  }, []);

  useEffect(() => {
    if (simulationContext.validationError) {
      const type = simulationContext.validationError.type;
      const property = simulationContext.validationError.property;

      switch (type) {
        case "call-settings": {
          if (property === "name") {
            setTimeout(() => {
              nameInputRef.current?.focus();
            }, 1000);
          } else if (property === "description") {
            setTimeout(() => {
              descriptionInputRef.current?.focus();
            }, 1000);
          } else if (property === "skills") {
            setShowSkillSlider(true);
          }

          simulationContext.gotoError?.(undefined);

          break;
        }

        default: {
          break;
        }
      }
    }
  }, [simulationContext.validationError]);

  const elevatorPitchConfiguration = {
    pitchScenario,
    pitchScenarioError,
    handlePitchScenarioChange,
    pitchAudience,
    pitchAudienceError,
    handlePitchAudienceChange,
    format,
    handleFormatChange,
    timeBound,
    handleTimeBoundChange,
  };
  
  const callConfiguration = {
    description,
    descriptionError,
    handleDescriptionChange,
    language,
    handlePropertyChange,
    duration,
    handleDurationChange,
    difficulty,
    handleDifficultyChange,
    format,
    handleFormatChange,
  };

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
      <div className={styles.left}>
        <div className={styles.details}>
          <div className={styles.headerContainer}>
            <div className={styles.header}>
              <div
                className={cx(styles.field, styles.thumbnail)}
                onClick={() => {
                  setEditThumbnail(true);
                }}
              >
                <div className={styles.inner}>
                  <div className={styles.overlay}>
                    <i className="paperflite-edit"></i>
                  </div>
                  <div
                    className={cx(
                      styles.pic,
                      (type === "discovery" && styles.discovery) ||
                        (type === "cold_call" && styles.coldcall) ||
                        (type === "elevator_pitch" && styles.elevatorPitch),
                    )}
                  >
                    <BannerView
                      banner={banner as any}
                      defaultBanner="/images/assets/simulationThumbnail.jpg"
                      overlay={false}
                    />
                  </div>
                </div>
                <ErrorMessage error={bannerError} />
              </div>
              <div className={cx(styles.field, styles.name)}>
                <TextareaAutosize
                  className={styles.input}
                  ref={nameInputRef}
                  placeholder="Scenario name goes here..."
                  rows={1}
                  maxRows={10}
                  value={name}
                  onChange={handleNameChange}
                  onPointerEnterCapture={() => {}}
                  onPointerLeaveCapture={() => {}}
                />
                <ErrorMessage error={nameError} />
              </div>
            </div>
            {(isPublished || publishedBy) &&
            <div className={styles.simulationMetadata}>
              {!pitchConfiguration && (
              <div className={styles.metadata}>
                <i className="paperflite-clock" />
                {`${simulation.duration} Minutes`}
              </div>
              )}
              {pitchConfiguration && (
                <div className={styles.metadata}>
                 <i className="paperflite-clock" />
                  {FileService.formatTimeBound(timeBound ?? 0)}
               </div>
              )}
              <div className={styles.metadata}>
                <i className="paperflite-calendar" />
                {callType}
              </div>
              {!pitchConfiguration && (
              <div className={styles.metadata}>
                <i className="paperflite-speedomenter" />
                {difficulty}
              </div>
              )}
              <div className={cx(styles.metadata, styles.profile)}>
                <Thumbnail
                  media={userProfile}
                  defaultImage="/images/assets/default_asset.jpg"
                  className={styles.icon}
                />
                by {publishedByName}
              </div>
            </div>
            }
          </div>

          {pitchConfiguration && (
            <PitchConfiguration
              configuration={elevatorPitchConfiguration}
            />
          )}
          {!pitchConfiguration && (
            <CallConfiguration
              configuration={callConfiguration}
            />
          )}
          
          <div className={cx(styles.field, styles.scoreCard)}>
            <div className={styles.heading}>
              <div className={styles.title}>
                <div className={styles.label}>
                  <Text text={`Assessment Framework - ${activeScorecard?.name ?? ""}`} lines={1} /></div>
                <p className={styles.description}>
                  <Text text={activeScorecard?.description || ""} lines={2} />
                </p>
              </div>
              <div className={styles.actions}>
                <div className={styles.actionItem}>
                  <i className={cx(styles.icon, "paperflite-eye")} />
                  <span onClick={() => handleEditAssessmentScoreCard(true)}>review</span>
                </div>
              </div>
            </div>
            <div className={styles.metadata}>
              <div className={styles.statItem}>
                <div className={styles.value}>{parameters.length.toString().padStart(2, "0")}</div>
                <div className={styles.label}>{pluralize("parameter", _.size(parameters))}</div>
              </div>
              <div className={styles.divider} />
              <div className={styles.statItem}>
                <div className={styles.value}>{subParameterCount.toString().padStart(2, "0")}</div>
                <div className={styles.label}>{pluralize("sub-parameter", subParameterCount)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      <div className={styles.right}>
        {/* {(isPublished || publishedBy) && (
          <SimulationMetricsCarousel simulation={simulation} />
        )} */}
        <p className={styles.heading}>
          Skills in focus ({simulation.skills?.length || 0})
        </p>
        <div className={styles.skills}>
        <div
            className={styles.addSkillButton}
            onClick={() => onSkillClicked()}
          >
            <i className={cx(styles.icon, "paperflite-add")}></i>
          </div>


          {(simulation.skills || []).map((skill: any) => {
            return (
              <div key={skill.id} className={styles.skill}>
                <div className={styles.name}>{skill.name}</div>
                <i
                  className={cx("paperflite-close", styles.closeIcon)}
                  onClick={() => handleSkillRemove(skill)}
                ></i>
              </div>
            );
          })}
        </div>
        <div className={styles.courseLearners}>
          <CourseLearners id={simulation.id!} course={simulation} />
        </div>
      </div>

      {editThumbnail && (
        <EditCourseThumbnail
          show={editThumbnail}
          type="simulation"
          entityType={getCallType(simulation?.type)}
          banner={simulation?.banner || null}
          defaultBanner="/images/assets/simulationThumbnail.jpg"
          onFinish={handleThumbnailChange}
          onClose={() => setEditThumbnail(false)}
        />
      )}

      {showSkillSlider && (
        <SelectSkillsDialog
          show={showSkillSlider}
          selected={simulation.skills || []}
          onFinish={handleAddSkillsFinish}
          onClose={() => setShowSkillSlider(false)}
        />
      )}
    </div>
  );
}


interface PreferredLanguageProps {
  value: string;
  onChange: (property: string, value: any) => void;
}

const PreferredLanguage = ({value, onChange}: PreferredLanguageProps) => {

  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const selectedLanguage =
      languageOptions.find(option => option.value === value) ||
      languageOptions[0];

    setSelected(selectedLanguage);
  }, [value]);

  const [selected, setSelected] = useState(languageOptions.find(option => option.value === value) || languageOptions[0]);
  const [open, setOpen] = useState(false);

  const animation = useSpring({
    height: open ? menuRef.current?.scrollHeight ?? 0 : 0,
    opacity: open ? 1 : 0,
    config: {
      tension: 120,
      friction: 30,
    },
  });

  const handleLanguageSelect = (language) => {
    onChange("language", language.value);
    setSelected(language);
    setOpen(false);
  }

  return (
    <div className={styles.languageDropdown}>
     <Dropdown show={open} onToggle={() => setOpen(prev => !prev)}>
        <Dropdown.Toggle
          id="preferred-language"
          as="button"
          className={styles.toggle}
        >
          {selected.label}
          <i className={cx("paperflite-dropdown", styles.dropdown)} />
        </Dropdown.Toggle>
      </Dropdown>

      <animated.div
        className={cx(styles.menuWrapper, {
          [styles.open]: open,
        })}
        style={animation}
      >
        <div className={styles.menu} ref={menuRef}>
          {languageOptions.map((language) => {
            const isSelected = selected.value === language.value;

            return (
              <div
                key={language.value}
                className={cx(styles.menuItem, {
                  [styles.selected]: isSelected,
                })}
                onClick={() => handleLanguageSelect(language)}
              >
                <i
                  className={cx(
                    isSelected
                      ? "paperflite-radio-button-selected"
                      : "paperflite-radio-button-unselected"
                  )}
                  aria-hidden="true"
                />
                <span>{language.label}</span>
              </div>
            );
          })}
        </div>
      </animated.div>
    </div>
  );
};

interface PitchConfigurationProps {
  configuration: {
    pitchScenario: string;
    pitchScenarioError: SimulationValidationError | null;
    handlePitchScenarioChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;

    pitchAudience: string;
    pitchAudienceError: SimulationValidationError | null;
    handlePitchAudienceChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;

    format: string;
    handleFormatChange: (value: string) => void;

    timeBound?: number;
    handleTimeBoundChange: (value: number) => void;
  };
}

const PitchConfiguration: React.FC<PitchConfigurationProps> = ({ configuration }) => {
  const {
    pitchScenario,
    pitchScenarioError,
    handlePitchScenarioChange,
    pitchAudience,
    pitchAudienceError,
    handlePitchAudienceChange,
    format,
    handleFormatChange,
    timeBound,
    handleTimeBoundChange,
  } = configuration;

  return (
    <>
      <div className={cx(styles.field, styles.description)}>
        <div className={styles.label}>Pitch Scenario</div>
        <TextareaAutosize
          className={styles.descriptionInput}
          rows={1}
          value={pitchScenario}
          placeholder="Define the pitch objective by describing the product, the target challenge, and the value message reps must deliver clearly."
          onChange={handlePitchScenarioChange}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
        <ErrorMessage error={pitchScenarioError} />
      </div>

      <div className={cx(styles.field, styles.description)}>
        <div className={styles.label}>Audience</div>
        <TextareaAutosize
          className={styles.descriptionInput}
          rows={1}
          value={pitchAudience}
          placeholder="Define who the rep is pitching to. Include the prospect’s role, company type, priorities, challenges, and what success looks like for them."
          onChange={handlePitchAudienceChange}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
        <ErrorMessage error={pitchAudienceError} />
      </div>

      <div className={cx(styles.field, styles.format)}>
        <div className={styles.label}>Format</div>
        <p className={styles.subtext}>
        Choose how the rep should deliver the pitch.
        </p>
        <SegmentButton
          listOfSegments={["Audio", "Video"]}
          selected={format}
          onChange={handleFormatChange}
        />
      </div>

      <div className={cx(styles.field, styles.duration)}>
        <div className={styles.label}>Duration</div>
        <p className={styles.subtext}>
          Set the maximum duration for the elevator pitch.
        </p>
        <Slide.Choice
          value={timeBound}
          options={TIME_BOUND_CHOICES}
          onChange={handleTimeBoundChange}
        />
      </div>
    </>
  );
};

interface CallConfigurationProps {
  configuration: {
    description: string;
    descriptionError: SimulationValidationError | null;
    handleDescriptionChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;

    language: string;
    handlePropertyChange: (property: string, value: any) => void;

    duration?: number;
    handleDurationChange: (value: number) => void;

    difficulty?: string;
    handleDifficultyChange: (value: string) => void;
    format: string;
    handleFormatChange: (value: string) => void;
  };
}

const CallConfiguration: React.FC<CallConfigurationProps> = ({ configuration }) => {
  const {
    description,
    descriptionError,
    handleDescriptionChange,
    language,
    handlePropertyChange,
    duration,
    handleDurationChange,
    difficulty,
    handleDifficultyChange,
    format,         
    handleFormatChange,
  } = configuration;

  return (
    <>
      <div className={cx(styles.field, styles.description)}>
        <div className={styles.label}>Description</div>
        <TextareaAutosize
          className={styles.descriptionInput}
          rows={1}
          value={description}
          placeholder= {"Describe the scenario to set the stage for your reps.\nInclude helpful context and key pointers to guide them through the simulation.\nMake it clear so they can confidently navigate the experience!..."}
          onChange={handleDescriptionChange}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
        <ErrorMessage error={descriptionError} />
      </div>

      <div className={cx(styles.field, styles.languageSettings)}>
        <div className={styles.label}>Preferred Language</div>
        <p className={styles.subtext}>
        Choose the language the prospect prefers for this call. The conversation will be guided in the selected language.
        </p>
        <PreferredLanguage
          value={language}
          onChange={handlePropertyChange}
        />
      </div>

      <div className={cx(styles.field, styles.duration)}>
        <div className={styles.label}>Call Duration</div>
        <p className={styles.subtext}>
        Set a time limit: short calls for specific skills, longer ones for
        full interactions
        </p>
        <Slide.Choice
          value={duration}
          options={DURATION_CHOICES}
          onChange={handleDurationChange}
        />
      </div>

      <div className={cx(styles.field, styles.difficulty)}>
        <div className={styles.label}>Challenge Level</div>
        <p className={styles.subtext}>
          Set the challenge level.
        </p>
        <Slide.Choice
          value={difficulty}
          options={DIFFICULTY_CHOICES}
          onChange={handleDifficultyChange}
        />
      </div>

      <div className={cx(styles.field, styles.format)}>
        <div className={styles.label}>Format</div>
        <p className={styles.subtext}>
          Choose how the rep should take this simulation.
        </p>
        <SegmentButton
          listOfSegments={["Audio", "Video"]}
          selected={format}
          onChange={handleFormatChange}
        />
      </div>
    </>
  );
};


